import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import TelegramIcon from "@material-ui/icons/Telegram";
import { toast } from "react-toastify";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  tableWrap: {
    overflowX: "auto"
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  muted: {
    color: theme.palette.text.secondary
  },
  chipPending: {
    backgroundColor: "#fff3cd",
    color: "#856404"
  },
  chipPendingPayment: {
    backgroundColor: "#d1ecf1",
    color: "#0c5460"
  },
  chipDraft: {
    backgroundColor: "#e2e3e5",
    color: "#383d41"
  },
  chipApproved: {
    backgroundColor: "#d4edda",
    color: "#155724"
  },
  chipRejected: {
    backgroundColor: "#f8d7da",
    color: "#721c24"
  },
  chipCancelled: {
    backgroundColor: "#ede7f6",
    color: "#4527a0"
  },
  chipActive: {
    backgroundColor: "#d4edda",
    color: "#155724"
  },
  chipInactive: {
    backgroundColor: "#f8d7da",
    color: "#721c24"
  },
  proofPreview: {
    width: "100%",
    maxHeight: "70vh",
    objectFit: "contain",
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default
  },
  proofFrame: {
    width: "100%",
    height: "70vh",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4
  },
  copyValue: {
    display: "inline-block",
    padding: theme.spacing(0.75, 1),
    border: `1px dashed ${theme.palette.primary.main}`,
    borderRadius: 4,
    cursor: "pointer",
    color: theme.palette.primary.main,
    backgroundColor: "rgba(25, 118, 210, 0.06)",
    maxWidth: 260,
    overflowWrap: "anywhere"
  }
}));

const emptyConfig = {
  botToken: "",
  isActive: false,
  botName: "",
  botUsername: "",
  welcomeMessage: "",
  visiblePlanIds: [],
  adminChatId: "",
  webhookUrl: "",
  webhookSecret: "",
  manualApprovalRules: {
    requirePaymentProof: true,
    notifyAdminChat: true
  },
  permissions: {
    registration: true,
    whatsappConnection: false,
    contactMessaging: false,
    groupsWa: false,
    whatsappStatus: false
  }
};

const emptyPaymentMethod = {
  name: "",
  description: "",
  instructions: "",
  accountData: "",
  isActive: true,
  displayOrder: 0
};

const emptyManualLink = {
  companyId: "",
  userId: "",
  telegramUserId: "",
  telegramChatId: "",
  telegramUsername: "",
  telegramFirstName: "",
  telegramLastName: ""
};

const TelegramBotConfig = () => {
  const classes = useStyles();
  const [config, setConfig] = useState(emptyConfig);
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [userLinks, setUserLinks] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [newPaymentMethod, setNewPaymentMethod] = useState(emptyPaymentMethod);
  const [manualLink, setManualLink] = useState(emptyManualLink);
  const [editingPaymentMethodId, setEditingPaymentMethodId] = useState(null);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(emptyPaymentMethod);
  const [previewProof, setPreviewProof] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configResponse, plansResponse, companiesResponse, paymentMethodsResponse, requestsResponse, linksResponse, auditResponse] = await Promise.all([
        api.get("/telegram-admin/config"),
        api.get("/plans/list"),
        api.get("/companies/list"),
        api.get("/telegram-admin/payment-methods"),
        api.get(`/telegram-admin/registration-requests?status=${statusFilter}`),
        api.get("/telegram-admin/user-links"),
        api.get("/telegram-admin/audit-logs")
      ]);

      setConfig({
        ...emptyConfig,
        ...configResponse.data,
        manualApprovalRules: {
          ...emptyConfig.manualApprovalRules,
          ...(configResponse.data.manualApprovalRules || {})
        },
        permissions: {
          ...emptyConfig.permissions,
          ...(configResponse.data.permissions || {})
        },
        visiblePlanIds: Array.isArray(configResponse.data.visiblePlanIds)
          ? configResponse.data.visiblePlanIds
          : []
      });
      setPlans(plansResponse.data || []);
      setCompanies(companiesResponse.data || []);
      setPaymentMethods(paymentMethodsResponse.data || []);
      setRegistrationRequests(requestsResponse.data || []);
      setUserLinks(linksResponse.data || []);
      setAuditLogs(auditResponse.data || []);
    } catch (error) {
      toastError(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    let isMounted = true;

    const loadUsers = async () => {
      if (!manualLink.companyId) {
        if (isMounted) {
          setCompanyUsers([]);
        }
        return;
      }

      setLoadingUsers(true);
      try {
        const { data } = await api.get("/users/list", {
          params: { companyId: manualLink.companyId }
        });

        if (isMounted) {
          setCompanyUsers(data || []);
        }
      } catch (error) {
        if (isMounted) {
          setCompanyUsers([]);
          toastError(error);
        }
      } finally {
        if (isMounted) {
          setLoadingUsers(false);
        }
      }
    };

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, [manualLink.companyId]);

  const updateConfigField = (field, value) => {
    setConfig((current) => ({
      ...current,
      [field]: value
    }));
  };

  const updatePermission = (field, value) => {
    setConfig((current) => ({
      ...current,
      permissions: {
        ...current.permissions,
        [field]: value
      }
    }));
  };

  const toggleVisiblePlan = (planId) => {
    setConfig((current) => {
      const visiblePlanIds = current.visiblePlanIds.includes(planId)
        ? current.visiblePlanIds.filter((id) => id !== planId)
        : [...current.visiblePlanIds, planId];

      return {
        ...current,
        visiblePlanIds
      };
    });
  };

  const saveConfig = async () => {
    if (config.isActive && !String(config.botToken || "").trim()) {
      toast.error("El token del bot es obligatorio para activarlo");
      return;
    }
    try {
      const { data } = await api.put("/telegram-admin/config", config);
      setConfig({
        ...emptyConfig,
        ...data,
        permissions: {
          ...emptyConfig.permissions,
          ...(data.permissions || {})
        }
      });
      toast.success("Configuracion del bot guardada");
    } catch (error) {
      toastError(error);
    }
  };

  const syncWebhook = async () => {
    try {
      await api.post("/telegram-admin/webhook/sync");
      toast.success("Webhook sincronizado con Telegram");
    } catch (error) {
      toastError(error);
    }
  };

  const createPaymentMethod = async () => {
    try {
      await api.post("/telegram-admin/payment-methods", newPaymentMethod);
      setNewPaymentMethod(emptyPaymentMethod);
      toast.success("Metodo de pago creado");
      loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const copyToClipboard = async (value) => {
    if (!value) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const input = document.createElement("textarea");
        input.value = value;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      toast.success("Datos copiados");
    } catch (error) {
      toast.error("No se pudo copiar");
    }
  };

  const togglePaymentMethod = async (paymentMethod) => {
    try {
      await api.put(`/telegram-admin/payment-methods/${paymentMethod.id}`, {
        ...paymentMethod,
        isActive: !paymentMethod.isActive
      });
      loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const startEditingPaymentMethod = (paymentMethod) => {
    setEditingPaymentMethodId(paymentMethod.id);
    setEditingPaymentMethod({
      name: paymentMethod.name || "",
      description: paymentMethod.description || "",
      instructions: paymentMethod.instructions || "",
      accountData: paymentMethod.accountData || "",
      isActive: paymentMethod.isActive !== false,
      displayOrder: paymentMethod.displayOrder || 0
    });
  };

  const cancelEditingPaymentMethod = () => {
    setEditingPaymentMethodId(null);
    setEditingPaymentMethod(emptyPaymentMethod);
  };

  const updateEditingPaymentMethod = (field, value) => {
    setEditingPaymentMethod((current) => ({
      ...current,
      [field]: value
    }));
  };

  const savePaymentMethod = async () => {
    try {
      await api.put(`/telegram-admin/payment-methods/${editingPaymentMethodId}`, {
        ...editingPaymentMethod,
        displayOrder: Number(editingPaymentMethod.displayOrder || 0)
      });
      toast.success("Metodo de pago actualizado");
      cancelEditingPaymentMethod();
      loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const approveRequest = async (requestId) => {
    if (!window.confirm("¿Aprobar esta solicitud de registro?")) {
      return;
    }
    try {
      await api.post(`/telegram-admin/registration-requests/${requestId}/approve`);
      toast.success("Solicitud aprobada");
      loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const rejectRequest = async (requestId) => {
    const reason = window.prompt("Motivo del rechazo");
    if (reason === null) return;

    try {
      await api.post(`/telegram-admin/registration-requests/${requestId}/reject`, { reason });
      toast.success("Solicitud rechazada");
      loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const downloadPaymentProof = async (paymentProof) => {
    try {
      const response = await api.get(`/telegram-admin/payment-proofs/${paymentProof.id}/download`, {
        responseType: "blob"
      });
      const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", paymentProof.fileName || `comprobante-${paymentProof.id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
      toastError(error);
    }
  };

  const previewPaymentProof = async (paymentProof) => {
    try {
      const response = await api.get(`/telegram-admin/payment-proofs/${paymentProof.id}/download`, {
        responseType: "blob"
      });
      const blob = new Blob([response.data], {
        type: paymentProof.mimeType || response.headers["content-type"] || "application/octet-stream"
      });
      const fileUrl = window.URL.createObjectURL(blob);
      setPreviewProof({
        ...paymentProof,
        fileUrl,
        mimeType: paymentProof.mimeType || blob.type
      });
    } catch (error) {
      toastError(error);
    }
  };

  const closePreviewProof = () => {
    if (previewProof?.fileUrl) {
      window.URL.revokeObjectURL(previewProof.fileUrl);
    }
    setPreviewProof(null);
  };

  const toggleUserLink = async (link) => {
    try {
      await api.put(`/telegram-admin/user-links/${link.id}`, {
        isActive: !link.isActive
      });
      loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const updateManualLinkField = (field, value) => {
    setManualLink((current) => ({
      ...current,
      [field]: value
    }));
  };

  const handleManualCompanyChange = (companyId) => {
    setManualLink((current) => ({
      ...current,
      companyId,
      userId: ""
    }));
  };

  const createManualLink = async () => {
    if (!manualLink.companyId || !manualLink.userId || !manualLink.telegramUserId || !manualLink.telegramChatId) {
      toast.error("Complete empresa, usuario e IDs de Telegram");
      return;
    }
    try {
      await api.post("/telegram-admin/user-links", {
        ...manualLink,
        companyId: Number(manualLink.companyId),
        userId: Number(manualLink.userId)
      });
      toast.success("Vinculo Telegram creado");
      setManualLink(emptyManualLink);
      setCompanyUsers([]);
      loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const requestStatusClass = (status) => {
    const classesByStatus = {
      pending: classes.chipPending,
      pending_payment: classes.chipPendingPayment,
      draft: classes.chipDraft,
      approved: classes.chipApproved,
      rejected: classes.chipRejected,
      cancelled: classes.chipCancelled
    };
    return classesByStatus[status] || classes.chipDraft;
  };

  const requestStatusLabel = (status) => {
    const labels = {
      pending: "Pendiente",
      pending_payment: "Pendiente de pago",
      draft: "Borrador",
      approved: "Aprobada",
      rejected: "Rechazada",
      cancelled: "Cancelada"
    };
    return labels[status] || status;
  };

  const allowedPlanIds = Array.isArray(config.visiblePlanIds)
    ? config.visiblePlanIds.map(Number)
    : [];
  const availableCompanies = allowedPlanIds.length > 0
    ? companies.filter((company) => allowedPlanIds.includes(Number(company.plan?.id || company.planId)))
    : companies;
  const selectedCompany = availableCompanies.find((company) => Number(company.id) === Number(manualLink.companyId));

  return (
    <Box>
      <Paper className={classes.section} elevation={1}>
        <div className={classes.titleRow}>
          <TelegramIcon color="primary" />
          <Typography variant="h6">Bot Telegram</Typography>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Token del bot"
              variant="outlined"
              size="small"
              fullWidth
              value={config.botToken || ""}
              onChange={(event) => updateConfigField("botToken", event.target.value)}
              helperText="Se oculta despues de guardar. Pega un token nuevo para cambiarlo."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Nombre"
              variant="outlined"
              size="small"
              fullWidth
              value={config.botName || ""}
              onChange={(event) => updateConfigField("botName", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Usuario del bot"
              variant="outlined"
              size="small"
              fullWidth
              value={config.botUsername || ""}
              onChange={(event) => updateConfigField("botUsername", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Webhook URL"
              variant="outlined"
              size="small"
              fullWidth
              value={config.webhookUrl || ""}
              onChange={(event) => updateConfigField("webhookUrl", event.target.value)}
              helperText={config.webhookSecret ? `Endpoint backend: /telegram/webhook/${config.webhookSecret}` : ""}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Chat admin para alertas"
              variant="outlined"
              size="small"
              fullWidth
              value={config.adminChatId || ""}
              onChange={(event) => updateConfigField("adminChatId", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Mensaje de bienvenida"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={config.welcomeMessage || ""}
              onChange={(event) => updateConfigField("welcomeMessage", event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(config.isActive)}
                  onChange={(event) => updateConfigField("isActive", event.target.checked)}
                  color="primary"
                />
              }
              label="Bot activo"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(config.permissions.registration)}
                  onChange={(event) => updatePermission("registration", event.target.checked)}
                  color="primary"
                />
              }
              label="Registro de empresas"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(config.permissions.whatsappConnection)}
                  onChange={(event) => updatePermission("whatsappConnection", event.target.checked)}
                  color="primary"
                />
              }
              label="Conexion WhatsApp"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(config.permissions.contactMessaging)}
                  onChange={(event) => updatePermission("contactMessaging", event.target.checked)}
                  color="primary"
                />
              }
              label="Mensajeria"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(config.permissions.groupsWa)}
                  onChange={(event) => updatePermission("groupsWa", event.target.checked)}
                  color="primary"
                />
              }
              label="Grupos WA"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(config.permissions.whatsappStatus)}
                  onChange={(event) => updatePermission("whatsappStatus", event.target.checked)}
                  color="primary"
                />
              }
              label="Estados WhatsApp"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Planes visibles desde Telegram</Typography>
            {plans.map((plan) => (
              <FormControlLabel
                key={plan.id}
                control={
                  <Checkbox
                    checked={config.visiblePlanIds.includes(plan.id)}
                    onChange={() => toggleVisiblePlan(plan.id)}
                    color="primary"
                  />
                }
                label={`${plan.name}${plan.value ? ` (${plan.value})` : ""}`}
              />
            ))}
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={saveConfig} disabled={loading}>
              Guardar configuracion
            </Button>
            <Box component="span" ml={1}>
              <Button variant="outlined" color="primary" onClick={syncWebhook} disabled={loading || !config.botToken}>
                Sincronizar webhook
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box p={2} borderRadius={8} border="1px solid rgba(0,0,0,0.12)">
              <Typography variant="subtitle2" gutterBottom>
                Flujo comercial disponible en el chat
              </Typography>
              <Typography variant="body2" className={classes.muted}>
                Con `Mensajeria` activa, el bot ya puede operar contactos y listas desde Telegram: sincronizar contactos, listarlos, agregarlos manualmente, importarlos por archivo, seleccionar varios o todos, crear listas y usarlas en campañas.
              </Typography>
              <Typography variant="body2" className={classes.muted}>
                Con `Grupos WA` activo, el bot permite sincronizar, guardar, listar y seleccionar grupos desde Telegram. Además, al crear campañas de grupos sincroniza la conexión elegida y abre selector múltiple para escogerlos.
              </Typography>
              <Typography variant="body2" className={classes.muted}>
                El menú del cliente ahora queda organizado por submenús: WhatsApp, Audiencias, Campañas y Reportes.
              </Typography>
              <Box mt={1} display="flex" flexWrap="wrap" gridGap={8}>
                <Chip size="small" label="/menu" />
                <Chip size="small" label="/contactos" />
                <Chip size="small" label="/sincronizar_contactos" />
                <Chip size="small" label="/agregar_contacto" />
                <Chip size="small" label="/importar_contactos" />
                <Chip size="small" label="/plantilla_contactos" />
                <Chip size="small" label="/listas_contactos" />
                <Chip size="small" label="/crear_lista_contactos" />
                <Chip size="small" label="/audiencias" />
                <Chip size="small" label="/sincronizar_grupos" />
                <Chip size="small" label="/grupos" />
                <Chip size="small" label="/nueva_campana" />
                <Chip size="small" label="/nueva_campana_grupos" />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.section} elevation={1}>
        <Typography variant="h6" gutterBottom>Metodos de pago</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Nombre"
              variant="outlined"
              size="small"
              fullWidth
              value={newPaymentMethod.name}
              onChange={(event) => setNewPaymentMethod({ ...newPaymentMethod, name: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Descripcion"
              variant="outlined"
              size="small"
              fullWidth
              value={newPaymentMethod.description}
              onChange={(event) => setNewPaymentMethod({ ...newPaymentMethod, description: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Datos de cuenta"
              variant="outlined"
              size="small"
              fullWidth
              value={newPaymentMethod.accountData}
              onChange={(event) => setNewPaymentMethod({ ...newPaymentMethod, accountData: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button variant="outlined" color="primary" onClick={createPaymentMethod} disabled={!newPaymentMethod.name}>
              Agregar metodo
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Instrucciones"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={newPaymentMethod.instructions}
              onChange={(event) => setNewPaymentMethod({ ...newPaymentMethod, instructions: event.target.value })}
            />
          </Grid>
        </Grid>

        <Box mt={2} className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Metodo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Datos de cuenta</TableCell>
                <TableCell>Instrucciones</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentMethods.map((method) => (
                <TableRow key={method.id}>
                  <TableCell>
                    {editingPaymentMethodId === method.id ? (
                      <TextField
                        label="Nombre"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={editingPaymentMethod.name}
                        onChange={(event) => updateEditingPaymentMethod("name", event.target.value)}
                      />
                    ) : (
                      method.name
                    )}
                  </TableCell>
                  <TableCell>
                    {editingPaymentMethodId === method.id ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(editingPaymentMethod.isActive)}
                            onChange={(event) => updateEditingPaymentMethod("isActive", event.target.checked)}
                            color="primary"
                          />
                        }
                        label="Activo"
                      />
                    ) : (
                      <Chip
                        size="small"
                        className={method.isActive ? classes.chipActive : classes.chipInactive}
                        label={method.isActive ? "Activo" : "Inactivo"}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {editingPaymentMethodId === method.id ? (
                      <TextField
                        label="Datos de cuenta"
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={editingPaymentMethod.accountData}
                        onChange={(event) => updateEditingPaymentMethod("accountData", event.target.value)}
                      />
                    ) : method.accountData ? (
                      <span
                        className={classes.copyValue}
                        title="Clic para copiar"
                        onClick={() => copyToClipboard(method.accountData)}
                      >
                        {method.accountData}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {editingPaymentMethodId === method.id ? (
                      <Box>
                        <TextField
                          label="Descripcion"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={editingPaymentMethod.description}
                          onChange={(event) => updateEditingPaymentMethod("description", event.target.value)}
                        />
                        <Box mt={1}>
                          <TextField
                            label="Instrucciones"
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            minRows={3}
                            value={editingPaymentMethod.instructions}
                            onChange={(event) => updateEditingPaymentMethod("instructions", event.target.value)}
                          />
                        </Box>
                        <Box mt={1}>
                          <TextField
                            label="Orden"
                            variant="outlined"
                            size="small"
                            type="number"
                            value={editingPaymentMethod.displayOrder}
                            onChange={(event) => updateEditingPaymentMethod("displayOrder", event.target.value)}
                          />
                        </Box>
                      </Box>
                    ) : (
                      method.instructions || method.description
                    )}
                  </TableCell>
                  <TableCell>
                    {editingPaymentMethodId === method.id ? (
                      <div className={classes.actions}>
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          onClick={savePaymentMethod}
                          disabled={!editingPaymentMethod.name}
                        >
                          Guardar
                        </Button>
                        <Button size="small" variant="outlined" onClick={cancelEditingPaymentMethod}>
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <div className={classes.actions}>
                        <Button size="small" color="primary" onClick={() => startEditingPaymentMethod(method)}>
                          Editar
                        </Button>
                        <Button size="small" onClick={() => togglePaymentMethod(method)}>
                          {method.isActive ? "Desactivar" : "Activar"}
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Paper className={classes.section} elevation={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Solicitudes de registro</Typography>
          <TextField
            select
            label="Estado"
            variant="outlined"
            size="small"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="pending">Pendientes</MenuItem>
            <MenuItem value="pending_payment">Pendiente de pago</MenuItem>
            <MenuItem value="draft">Borrador</MenuItem>
            <MenuItem value="approved">Aprobadas</MenuItem>
            <MenuItem value="rejected">Rechazadas</MenuItem>
            <MenuItem value="cancelled">Canceladas</MenuItem>
          </TextField>
        </Box>
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Responsable</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Pago</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Comprobante</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {registrationRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{request.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{request.companyName}</Typography>
                    <Typography variant="caption" className={classes.muted}>{request.email}</Typography>
                  </TableCell>
                  <TableCell>{request.responsibleName}</TableCell>
                  <TableCell>{request.plan?.name || request.planId}</TableCell>
                  <TableCell>{request.paymentMethod?.name || request.paymentMethodId}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      className={requestStatusClass(request.status)}
                      label={requestStatusLabel(request.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {(request.paymentProofs || []).length > 0 ? (
                      <div className={classes.actions}>
                        {(request.paymentProofs || []).map((proof) => (
                          <React.Fragment key={proof.id}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => previewPaymentProof(proof)}
                            >
                              Vista previa
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => downloadPaymentProof(proof)}
                            >
                              Descargar
                            </Button>
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      "Sin archivo"
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={classes.actions}>
                      <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        disabled={request.status !== "pending"}
                        onClick={() => approveRequest(request.id)}
                      >
                        Aprobar
                      </Button>
                      <Button
                        size="small"
                        color="secondary"
                        variant="outlined"
                        disabled={request.status === "approved" || request.status === "rejected"}
                        onClick={() => rejectRequest(request.id)}
                      >
                        Rechazar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Paper>

      <Paper className={classes.section} elevation={1}>
        <Typography variant="h6" gutterBottom>Usuarios Telegram vinculados</Typography>
        <Divider />
        <Box mt={2} mb={2} p={2} borderRadius={8} border="1px solid rgba(0,0,0,0.12)">
          <Typography variant="subtitle2" gutterBottom>Vincular Telegram manualmente</Typography>
          <Typography variant="body2" className={classes.muted}>
            Pídele al usuario que abra el bot y ejecute <strong>/mi_chat_id</strong>. Con ese dato puedes asociarlo a una empresa ya existente, siempre que esa empresa tenga un plan habilitado para Telegram.
          </Typography>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Empresa"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={manualLink.companyId}
                  onChange={(event) => handleManualCompanyChange(event.target.value)}
                  helperText={
                    allowedPlanIds.length > 0
                      ? "Solo se muestran empresas con planes visibles en Telegram."
                      : "La validación final del plan se hace al guardar."
                  }
                >
                  <MenuItem value="">Selecciona una empresa</MenuItem>
                  {availableCompanies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name} {company.plan?.name ? `• ${company.plan.name}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Usuario interno"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={manualLink.userId}
                  onChange={(event) => updateManualLinkField("userId", event.target.value)}
                  disabled={!manualLink.companyId || loadingUsers}
                  helperText={!manualLink.companyId ? "Primero elige una empresa." : loadingUsers ? "Cargando usuarios..." : ""}
                >
                  <MenuItem value="">Selecciona un usuario</MenuItem>
                  {companyUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} {user.email ? `• ${user.email}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Telegram User ID"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={manualLink.telegramUserId}
                  onChange={(event) => updateManualLinkField("telegramUserId", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Telegram Chat ID"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={manualLink.telegramChatId}
                  onChange={(event) => updateManualLinkField("telegramChatId", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Username Telegram"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={manualLink.telegramUsername}
                  onChange={(event) => updateManualLinkField("telegramUsername", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Nombre"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={manualLink.telegramFirstName}
                  onChange={(event) => updateManualLinkField("telegramFirstName", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Apellido"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={manualLink.telegramLastName}
                  onChange={(event) => updateManualLinkField("telegramLastName", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="body2" className={classes.muted}>
                  {selectedCompany
                    ? `Empresa seleccionada: ${selectedCompany.name}${selectedCompany.plan?.name ? ` • Plan: ${selectedCompany.plan.name}` : ""}`
                    : "Completa empresa, usuario y los IDs de Telegram para crear el vínculo."}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={createManualLink}
                    disabled={!manualLink.companyId || !manualLink.userId || !manualLink.telegramUserId || !manualLink.telegramChatId}
                  >
                    Crear vinculo
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box mt={2} className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Telegram</TableCell>
                <TableCell>Usuario interno</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Ultimo acceso</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userLinks.map((link) => (
                <TableRow key={link.id}>
                  <TableCell>
                    {link.telegramUsername ? `@${link.telegramUsername}` : link.telegramUserId}
                  </TableCell>
                  <TableCell>{link.user?.name || link.userId}</TableCell>
                  <TableCell>{link.company?.name || link.companyId}</TableCell>
                  <TableCell>{link.lastAccessAt ? new Date(link.lastAccessAt).toLocaleString() : "Sin acceso"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      className={link.isActive ? classes.chipActive : classes.chipInactive}
                      label={link.isActive ? "Vinculado" : "Bloqueado"}
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => toggleUserLink(link)}>
                      {link.isActive ? "Bloquear" : "Activar"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Paper className={classes.section} elevation={1}>
        <Typography variant="h6" gutterBottom>Auditoria del bot</Typography>
        <Divider />
        <Box mt={2} className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Accion</TableCell>
                <TableCell>Telegram</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Entidad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{log.createdAt ? new Date(log.createdAt).toLocaleString() : ""}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>{log.telegramUserId || "-"}</TableCell>
                  <TableCell>{log.user?.name || log.userId || "-"}</TableCell>
                  <TableCell>{log.company?.name || log.companyId || "-"}</TableCell>
                  <TableCell>{log.entity ? `${log.entity} #${log.entityId || ""}` : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Dialog open={Boolean(previewProof)} onClose={closePreviewProof} maxWidth="md" fullWidth>
        <DialogTitle>
          Comprobante {previewProof?.fileName || (previewProof?.id ? `#${previewProof.id}` : "")}
        </DialogTitle>
        <DialogContent>
          {previewProof?.mimeType?.startsWith("image/") ? (
            <img
              src={previewProof.fileUrl}
              alt={previewProof.fileName || "Comprobante de pago"}
              className={classes.proofPreview}
            />
          ) : previewProof?.mimeType === "application/pdf" ? (
            <iframe
              title={previewProof.fileName || "Comprobante de pago"}
              src={previewProof.fileUrl}
              className={classes.proofFrame}
            />
          ) : (
            <Typography>
              Este archivo no tiene vista previa disponible. Puedes descargarlo para revisarlo.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {previewProof ? (
            <Button onClick={() => downloadPaymentProof(previewProof)} color="primary">
              Descargar
            </Button>
          ) : null}
          <Button onClick={closePreviewProof} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TelegramBotConfig;
